import { blockCategories } from '../../categories';
import { DialogService } from 'aurelia-dialog';
import { Container } from 'aurelia-framework';
import { UniversalEntitySelect } from '../../../../../dialog/universal-entity-select';
import { content } from '../../../../../../tailwind.config';
import { AspectRatio } from '../../../../../../node_modules/@mui/icons-material/index';
import {
    img_src_default,
    imageTraits,
} from '../../bootstrap-components/components/Image.js';
import createStyleSyncService from '../../../components/traits/services/styleSyncService';

export default function (editor, opts = {}) {
    const blockManager = editor.BlockManager;
    const domComponents = editor.DomComponents;
    const traitManager = editor.TraitManager;
    const imageType = domComponents.getType('image');
    const view = imageType.view;
    const model = imageType.model;

    opts = {
        category: blockCategories.basic,
        label: 'Galerie',
        block: {},
        props: {},
        style: {},
        ...opts,
    };

    const dialogService = Container.instance.get(DialogService);
    const styleSyncService = createStyleSyncService(editor);

    const parseRepeatCount = (value) => {
        const match = String(value || '').match(/repeat\(\s*(\d+)/i);
        return match ? parseInt(match[1], 10) : null;
    };

    traitManager.addType('gallery-columns-trait', {
        noLabel: true,
        createInput({ trait }) {
            const component = trait.target;
            const property = 'grid-template-columns';
            const min = 1;
            const max = 12;
            const defaultCols = 3;

            const readCols = () => {
                const raw = styleSyncService.readPropertyValue({ component, property, unit: '' });
                const parsed = parseRepeatCount(raw);
                return Number.isFinite(parsed) ? parsed : defaultCols;
            };

            const wrapper = document.createElement('div');
            wrapper.style.cssText = 'display:flex;flex-direction:column;gap:0px;width:100%;margin-top:4px;margin-bottom:4px;';

            const title = document.createElement('div');
            title.textContent = 'Columns';
            title.style.cssText = 'font-size:14px;line-height:1.3;margin:2px 0px;';

            const controlsRow = document.createElement('div');
            controlsRow.style.cssText = 'display:flex;align-items:center;gap:8px;width:100%;';

            const slider = document.createElement('input');
            slider.type = 'range';
            slider.min = min;
            slider.max = max;
            slider.step = 1;
            slider.value = readCols();
            slider.style.cssText = 'flex:1;min-width:0;accent-color:#0991b2;cursor:pointer;margin:2px;appearance:auto;';

            const numberInput = document.createElement('input');
            numberInput.type = 'number';
            numberInput.min = min;
            numberInput.max = max;
            numberInput.step = 1;
            numberInput.value = readCols();
            numberInput.style.cssText = 'width:58px;background:#2a2a3e;border:1px solid #444;border-radius:4px;color:#fff;font-size:14px;padding:3px 5px;text-align:center;outline:none;';

            const syncFromStyles = () => {
                const cols = readCols();
                const clamped = Math.max(min, Math.min(max, cols));
                slider.value = clamped;
                numberInput.value = clamped;
            };

            const applyValue = (val) => {
                const parsed = parseInt(val, 10);
                if (!Number.isFinite(parsed)) return;
                const clamped = Math.max(min, Math.min(max, parsed));
                slider.value = clamped;
                numberInput.value = clamped;
                styleSyncService.applyStyleValue({
                    component,
                    property,
                    value: `repeat(${clamped}, 1fr)`,
                });
            };

            wrapper._unsubscribeDeviceChange = styleSyncService.subscribeToDeviceChangesForElement({
                element: wrapper,
                onChange: syncFromStyles,
            });

            slider.addEventListener('input', () => applyValue(slider.value));
            numberInput.addEventListener('change', () => applyValue(numberInput.value));
            numberInput.addEventListener('input', () => applyValue(numberInput.value));

            controlsRow.append(slider, numberInput);
            wrapper.append(title, controlsRow);
            wrapper._syncFromStyles = syncFromStyles;

            return wrapper;
        },

        onUpdate({ elInput }) {
            if (typeof elInput._syncFromStyles === 'function') {
                elInput._syncFromStyles();
            }
        },

        onEvent() { },
    });

    domComponents.addType('gallery-image', {
        extend: 'image',
        model: {
            defaults: Object.assign({}, model.prototype.defaults, {
                'custom-name': 'Gallery Image',
                tagName: 'img',
                resizable: 1,
                attributes: {
                    src: img_src_default,
                },
                classes: ['sio-image', 'sio-gallery-image', 'max-w-full', 'h-auto'],
                traits: imageTraits.concat(model.prototype.defaults.traits),
            }),
        },
        isComponent: function (el) {
            if (el && el.tagName === 'IMG' && el.classList &&
                el.classList.contains('sio-gallery-image')) {
                return { type: 'gallery-image' };
            }
        },
        view: Object.assign({}, view, {
            events: {
                dblclick: 'openAssetManager',
            },
            openAssetManager() {
                const am = this.model.em.get('AssetManager');
                am.open({
                    select: (image) => {
                        this.model.set('src', image.getSrc());
                        am.close();
                    },
                });
            },
        }),
    });

    domComponents.addType('gallery-item', {
        model: {
            defaults: {
                tagName: 'div',
                draggable: ['gallery-container'],
                droppable: false,
                attributes: { class: 'gallery-item' },
                traits: [],
                label: 'Gallery Item',
                components: [
                    {
                        tagName: 'img',
                        type: 'gallery-image',
                        editable: false,
                        draggable: false,
                        droppable: false,
                        attributes: {
                            class: 'gallery-img',
                            alt: '',
                        },
                        style: {
                            width: '100%',
                            height: '100%',
                            'object-fit': 'cover',
                            display: 'block',
                        },
                    },
                ],
                style: {
                    overflow: 'hidden',
                    AspectRatio: '1/1',
                },
            },
        },
    });

    domComponents.addType('gallery-container', {
        model: {
            defaults: {
                tagName: 'div',
                draggable: true,
                droppable: ['gallery-item'],
                attributes: { class: 'gallery-container' },
                label: 'Gallery',
                traits: [
                    {
                        type: 'gallery-columns-trait',
                        name: 'gallery-columns',
                        label: false,
                    },
                    {
                        label: 'Gap',
                        type: 'number',
                        name: 'gallery-gap',
                        default: '0',
                        changeProp: 1,
                    },
                    {
                        label: 'Aspect ratio',
                        type: 'select',
                        name: 'gallery-aspect',
                        options: [
                            { value: '1/1', name: '1:1 (Quadrat)' },
                            { value: '3/2', name: '3:2 (Landscape)' },
                            { value: '4/3', name: '4:3 (Landscape)' },
                            { value: '16/9', name: '16:9 (Landscape)' },
                            { value: '2/3', name: '2:3 (Portrait)' },
                            { value: '3/4', name: '3:4 (Portrait)' },
                            { value: '9/16', name: '9:16 (Portrait)' },
                        ],
                        changeProp: 1,
                    },
                    {
                        label: 'Border radius',
                        type: 'number',
                        name: 'gallery-radius',
                        default: '0',
                        changeProp: 1,
                    },
                    // Bulk image upload
                    {
                        type: 'gallery-bulk-select',
                        name: 'gallery-bulk-select',
                        label: 'Bilder',
                    },
                ],
                // Default trait values
                'gallery-gap': '0',
                'gallery-aspect': '1/1',
                'gallery-radius': '0',
                components: [
                    { type: 'gallery-item' },
                    { type: 'gallery-item' },
                    { type: 'gallery-item' },
                ],
                style: {
                    display: 'grid',
                    'grid-template-columns': 'repeat(3, 1fr)',
                    width: '100%',
                },
            },

            init() {
                this.on('change:gallery-gap', this.updateGrid);
                this.on('change:gallery-aspect', this.updateGridItem);
                this.on('change:gallery-radius', this.updateGridItem);
                this.updateGrid();
                this.updateGridItem();
            },

            updateGrid() {
                const gap = this.get('gallery-gap') || '0';
                this.addStyle({
                    display: 'grid',
                    width: '100%',
                    gap: `${gap}px`,
                });
            },

            updateGridItem() {
                const aspect = this.get('gallery-aspect') || '1/1';
                const radius = this.get('gallery-radius') || '0';
                // Apply padding-bottom trick to every gallery-item child
                this.components().each((child) => {
                    if (child.get('type') === 'gallery-item') {
                        child.addStyle({
                            'aspect-ratio': aspect,
                            'border-radius': `${radius}px`,
                        });
                        // And the inner img
                        const img = child.components().first();
                        if (img) {
                            img.addStyle({
                                width: '100%',
                                height: '100%',
                                'object-fit': 'cover',
                            });
                        }
                    }
                });
            },
        },

        view: {
            init() {
                this.listenTo(this.model, 'change:gallery-gap', this.render);
                this.listenTo(this.model, 'change:gallery-aspect', this.render);
                this.listenTo(this.model, 'change:gallery-radius', this.render);
            },
        },
    });

    traitManager.addType('gallery-bulk-select', {
        noLabel: true,
        createInput({ trait }) {
            const wrapper = document.createElement('div');
            const button = document.createElement('button');
            button.innerText = 'Bilder hinzufügen';
            button.className = 'gjs-btn-prim';
            button.style.cssText =
                'width:100%;margin-top:6px;padding:6px 10px;cursor:pointer;';

            button.addEventListener('click', (e) => {
                e.preventDefault();

                if (!dialogService || !UniversalEntitySelect) {
                    console.warn(
                        '[gallery-block] dialogService or UniversalEntitySelect not provided in opts.'
                    );
                    return;
                }

                const component = trait.target;

                dialogService
                    .open({
                        viewModel: UniversalEntitySelect,
                        model: {
                            selectModelId: 'media-database/media-item',
                            conditions: { type: { $in: ['image'] } },
                        },
                    })
                    .whenClosed((data) => {
                        const items = data.output;
                        if (!items || items.length === 0) return;

                        items.forEach((item) => {
                            const src =
                                item?.file?.downloadUrl ||
                                (item?.file?.previewUrl
                                    ? item.file.previewUrl
                                        .replace('$width', '1000')
                                        .replace('$height', '-')
                                    : null);

                            if (!src) return;

                            component.append({
                                type: 'gallery-item',
                                components: [
                                    {
                                        tagName: 'img',
                                        type: 'gallery-image',
                                        editable: false,
                                        draggable: false,
                                        droppable: false,
                                        attributes: {
                                            class: 'gallery-img',
                                            src,
                                            alt: item?.name || '',
                                        },
                                        style: {
                                            width: '100%',
                                            height: '100%',
                                            'object-fit': 'cover',
                                            display: 'block',
                                        },
                                    },
                                ],
                            });

                            component.updateGridItem();
                        });
                    });
            });

            wrapper.appendChild(button);
            return wrapper;
        },
        onEvent() { },
        onUpdate() { },
    });

    blockManager.add('gallery-block', {
        label: opts.label,
        category: opts.category,
        media: `<svg viewBox="0 0 24 24">
            <path fill="currentColor" d="M3 3h7v7H3V3m0 11h7v7H3v-7m11-11h7v7h-7V3m0 11h7v7h-7v-7Z"/>
        </svg>`,
        content: {
            type: 'gallery-container',
        },
        ...opts.block,
    });

    editor.Commands.add('gallery:add-image', {
        run(editor) {
            const selected = editor.getSelected();
            if (selected?.get('type') === 'gallery-container') {
                selected.append({ type: 'gallery-item' });
            }
        },
    });

    editor.on('component:selected', (component) => {
        const toolbar = component.get('toolbar') || [];

        if (component.get('type') === 'gallery-container') {
            const hasAdd = toolbar.some(
                (btn) => btn.command === 'gallery:add-image'
            );
            if (!hasAdd) {
                component.set('toolbar', [
                    ...toolbar,
                    {
                        attributes: {
                            class: 'fa fa-plus toolbar-icon',
                            title: 'Bild hinzufügen',
                        },
                        command: 'gallery:add-image',
                        label: '',
                    },
                ]);
            }
        }
    });
}
